import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Convocatoria from './torneos/Convocatoria';
import AvisoDeportesFaltantes from './deportes/AvisoDeportesFaltantes';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { usePlatformBranding } from '../hooks/usePlatformBranding';

const ESTATUS_LABEL = {
  aceptada: 'Esta invitación ya fue aceptada.',
  rechazada: 'Esta invitación fue rechazada.',
  cancelada: 'Esta invitación fue cancelada por el organizador.',
};

// A nivel de módulo (NO dentro del componente): definirlo dentro re-monta el subárbol en
// cada render y el formulario perdería el foco al escribir.
const Shell = ({ branding, children }) => (
  <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: branding.bg }}>
    <Card className="w-full max-w-md"><CardContent className="p-8">
      <img src={branding.logo} alt={branding.name} className="h-12 mx-auto mb-4 object-contain" />
      {children}
    </CardContent></Card>
  </div>
);

export default function AceptarInvitacionTorneo() {
  const branding = usePlatformBranding();
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';

  // Estado de la invitación (se consulta ANTES de mostrar el formulario, para no pedir crear
  // usuario cuando el club ya tiene cuenta — ese caso va por "inicia sesión y acepta en tu app").
  const [estado, setEstado] = useState(null);
  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState(null);

  const [clubNombre, setClubNombre] = useState('');
  const [adminNombre, setAdminNombre] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Si ya hay sesión, el club existente puede aceptar sin salir de esta pantalla.
  const [sesion, setSesion] = useState(null);
  // Lo que se está aceptando: categorías, fechas, topes. Antes se aceptaba a ciegas.
  const [convocatoria, setConvocatoria] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (vivo) setSesion(data?.session || null);
    });
    return () => { vivo = false; };
  }, []);

  // El token ya prueba que invitaron a este club: no se exige además que el correo
  // de la sesión sea el mismo al que se envió la invitación.
  const aceptarConSesion = async () => {
    setError(null);
    setLoading(true);
    const { error: rpcErr } = await supabase.rpc('aceptar_invitacion_torneo_por_token', { p_token: token });
    setLoading(false);
    if (rpcErr) {
      setError(rpcErr.message || 'No se pudo aceptar la invitación.');
      return;
    }
    navigate('/torneos');
  };

  useEffect(() => {
    if (!token) { setChecking(false); setCheckError('Este link de invitación no es válido.'); return; }
    let cancel = false;
    (async () => {
      const { data, error: rpcErr } = await supabase.rpc('get_invitacion_estado', { p_token: token });
      supabase.rpc('get_convocatoria', { p_token: token }).then(({ data: conv }) => {
        if (conv) setConvocatoria(conv);
      });
      if (cancel) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (rpcErr || !row) setCheckError('Este link de invitación no es válido o expiró.');
      else setEstado(row);
      setChecking(false);
    })();
    return () => { cancel = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token) { setError('Link inválido.'); return; }
    if (!clubNombre.trim()) { setError('Escribe el nombre del club.'); return; }
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    const { data, error: fErr } = await supabase.functions.invoke('aceptar-invitacion-torneo', {
      body: { token, club_nombre: clubNombre.trim(), admin_nombre: adminNombre.trim(), password },
    });
    setLoading(false);
    if (fErr || data?.error) {
      let body = data;
      if (!body && fErr?.context?.response) {
        try {
          const cloned = fErr.context.response.clone();
          const text = await cloned.text();
          if (text) {
            try { body = JSON.parse(text); } catch { body = { error: text }; }
          }
        } catch (parseErr) {
          console.error('Error parsing function response:', parseErr);
        }
      }
      if (body?.existing) { setError('Ese correo ya tiene cuenta. Inicia sesión y acepta la invitación desde tu app.'); return; }
      setError(body?.error || fErr?.message || 'No se pudo aceptar la invitación.'); return;
    }
    setSuccess(true);
    setTimeout(() => navigate('/login'), 2000);
  };

  if (checking) return <Shell branding={branding}><p className="text-center text-muted-foreground">Cargando invitación...</p></Shell>;

  if (success) return (
    <Shell branding={branding}>
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">¡Club registrado!</h2>
        <p className="text-muted-foreground">Ya puedes iniciar sesión. Redirigiendo...</p>
      </div>
    </Shell>
  );

  if (checkError) return (
    <Shell branding={branding}>
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Invitación no disponible</h1>
        <p className="text-muted-foreground">{checkError}</p>
      </div>
    </Shell>
  );

  // Invitación ya respondida (aceptada/rechazada/cancelada).
  if (estado && estado.estatus !== 'pendiente') return (
    <Shell branding={branding}>
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Invitación a {estado.torneo_nombre || 'un torneo'}</h1>
        <p className="text-muted-foreground mb-6">{ESTATUS_LABEL[estado.estatus] || 'Esta invitación ya no está disponible.'}</p>
        <Button className="w-full" onClick={() => navigate('/login')}>Ir a MYKLUBB</Button>
      </div>
    </Shell>
  );

  // El club ya tiene cuenta (camino B).
  //
  // Antes esto era un callejón sin salida: mandaba a "acepta desde tu app", pero
  // adentro la invitación solo se ve si el correo invitado es EXACTAMENTE el de la
  // sesión. Si el organizador invitó al correo de contacto del club y el admin entra
  // con el suyo, la invitación es invisible y nadie sabe por qué.
  //
  // El token que trae este enlace ya prueba que los invitaron, así que si hay sesión
  // se acepta aquí mismo. Sin sesión, se manda a entrar y se vuelve con el token.
  if (estado && estado.ya_tiene_cuenta) return (
    <Shell branding={branding}>
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Invitación a {estado.torneo_nombre || 'un torneo'}</h1>
        {convocatoria && (
          <div className="mb-6 rounded-lg border p-4">
            <Convocatoria convocatoria={convocatoria} />
          </div>
        )}
        {sesion ? (
          <>
            <p className="text-muted-foreground mb-6">
              Tu club ya tiene cuenta en MYKLUBB. Acepta la invitación aquí mismo.
            </p>
            <AvisoDeportesFaltantes deportes={convocatoria?.deportes_faltantes} className="mb-4 text-left" />
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            <Button className="w-full" onClick={aceptarConSesion} disabled={loading}>
              {loading ? 'Aceptando...' : 'Aceptar invitación'}
            </Button>
          </>
        ) : (
          <>
            <p className="text-muted-foreground mb-6">
              Tu club ya tiene cuenta en MYKLUBB. Inicia sesión y vuelve a abrir este enlace
              para aceptar.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Iniciar sesión
            </Button>
          </>
        )}
      </div>
    </Shell>
  );

  // Club sin cuenta: registrar club + admin (camino A).
  return (
    <Shell branding={branding}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-1">Invitación a {estado?.torneo_nombre || 'un torneo'}</h1>
        <p className="text-muted-foreground">Registra tu club para participar en el torneo.</p>
      </div>
      {convocatoria && (
        <div className="mb-6 rounded-lg border p-4">
          <Convocatoria convocatoria={convocatoria} />
        </div>
      )}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-red-700 text-sm">{error}</p></div>}
      <form onSubmit={submit} className="space-y-4">
        <div><label className="block text-sm font-medium mb-2">Nombre del club</label>
          <input value={clubNombre} onChange={(e) => setClubNombre(e.target.value)} className="w-full px-4 py-2 border border-input rounded-md" placeholder="Ej: Lobos FC" required disabled={loading} /></div>
        <div><label className="block text-sm font-medium mb-2">Tu nombre</label>
          <input value={adminNombre} onChange={(e) => setAdminNombre(e.target.value)} className="w-full px-4 py-2 border border-input rounded-md" placeholder="Nombre del administrador" disabled={loading} /></div>
        <div><label className="block text-sm font-medium mb-2">Contraseña</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 border border-input rounded-md" placeholder="Mínimo 6 caracteres" required disabled={loading} /></div>
        <div><label className="block text-sm font-medium mb-2">Confirmar contraseña</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full px-4 py-2 border border-input rounded-md" required disabled={loading} /></div>
        <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Registrando...' : 'Aceptar y registrar club'}</Button>
      </form>
    </Shell>
  );
}
